import type { ChannelFixed } from "../api/laboperator.types.ts";
import type { Channel, FumeHoodState } from "../view-model.ts";

type SuffixGroupIdentifier =
  | "1_left"
  | "2_right"
  | "1_first"
  | "2_second"
  | "3_third"
  | "4_fourth"
  | "5_fifth"
  | "6_sixth"
  | "7_seventh"
  | "8_eighth"
  | "9_ninth"
  | "10_tenth"
  | "11_eleventh"
  | "12_twelfth";

type ChannelGroupIdentifier = "1_container" | "2_internal";

export type GroupIdentifier = SuffixGroupIdentifier | ChannelGroupIdentifier;

// Groups for suffix-based channels (sphere, spider)
const SUFFIX_GROUPS = {
  // sphere
  left: "1_left",
  right: "2_right",
  // spider, sensor
  first: "1_first",
  second: "2_second",
  third: "3_third",
  fourth: "4_fourth",
  fifth: "5_fifth",
  sixth: "6_sixth",
  seventh: "7_seventh",
  eighth: "8_eighth",
  ninth: "9_ninth",
  tenth: "10_tenth",
  eleventh: "11_eleventh",
  twelfth: "12_twelfth",
} as const;

const CHANNEL_SUFFIXES = new Set(
  Object.keys(SUFFIX_GROUPS).map((suffix) => `_${suffix}`)
);

// Groups for specific channel names (fumehood, rührer)
const CHANNEL_NAME_TO_GROUP: Record<ChannelGroupIdentifier, string[]> = {
  // fumehood
  "1_container": ["solvent_container_1", "solvent_container_2"],
  "2_internal": ["angle_flap", "temperature"],
};

export const UNGROUPED_CHANNEL_ORDER = [
  "operation_status",
  "light",
  "airflow",
  null, // This will be an empty spot
  "slider_vertical",
  "slider_horizontal",
] as const;

export const createViewModelChannel = (
  channel: ChannelFixed,
  name: string,
  description = "",
  hideEvalutaion = false
): Channel => {
  let channelName = name;
  let suffix = undefined;
  let group: GroupIdentifier | undefined = undefined;

  // Handle suffixes (like _left, _right) - this takes precedence
  for (const channelSuffix of CHANNEL_SUFFIXES) {
    if (channelName.endsWith(channelSuffix)) {
      channelName = channelName.slice(0, -channelSuffix.length);
      suffix = channelSuffix.slice(1);
      group = SUFFIX_GROUPS[suffix as keyof typeof SUFFIX_GROUPS];
      // If we found a suffix-based group, return immediately
      return {
        id: `${channel.id}`,
        name: channelName,
        suffix,
        group,
        unit: channel.attributes?.unit,
        status: channel.attributes?.status || "unknown",
        interval: channel.attributes?.interval,
        description,
        hideEvalutaion,
      };
    }
  }

  for (const [groupName, channels] of Object.entries(CHANNEL_NAME_TO_GROUP)) {
    if (channels.includes(channelName)) {
      group = groupName as ChannelGroupIdentifier;
      break;
    }
  }

  return {
    id: `${channel.id}`,
    name: channelName,
    suffix,
    group,
    unit: channel.attributes?.unit,
    status: channel.attributes?.status || "unknown",
    interval: channel.attributes?.interval,
    description,
    hideEvalutaion,
  };
};

export function mapOnlineState(
  status: string | undefined
): FumeHoodState["status"] {
  switch (status) {
    case "online":
    case "on": {
      return "online";
    }
    case "offline":
    case "off": {
      return "offline";
    }
    default: {
      return "error";
    }
  }
}
